import { useCallback, useEffect, useState } from 'react';
import { Loader2, Landmark, Play, Settings, XCircle, CheckCircle2, PowerOff, Search, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { xeroBankFeedAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { XeroBankFeedConfigureModal } from './XeroBankFeedConfigureModal';
import { useJobPolling } from './JobPollingPanel';

const STATUS_LABEL = {
  ok: 'OK',
  failed: 'Failed',
  running: 'Running',
};

const STATUS_VARIANT = {
  ok: 'default',
  failed: 'destructive',
  running: 'outline',
};

const COVERAGE_DIAGNOSIS_STYLE = {
  valid_id_zero_coded: 'text-amber-600 dark:text-amber-400',
  different_account_has_transactions: 'text-amber-600 dark:text-amber-400',
  transactions_present: 'text-emerald-700 dark:text-emerald-400',
  inconclusive: 'text-red-600',
};

function TraceResultPanel({ mappingId, trace }) {
  const present = trace.mapped_account_id_present_in_pull;
  const reasonEntries = Object.entries(trace.reason_tally || {});
  const diagnosis = trace.coverage_diagnosis;

  return (
    <div className="mt-2 border-t pt-3 space-y-3" data-testid={`xero-bankfeed-trace-panel-${mappingId}`}>
      <p className="text-[11px] text-muted-foreground italic">
        Read-only diagnostic -- makes no changes. Never writes to accounting_reconciliation
        or any mapping/run doc.
      </p>

      <div
        className={`flex items-start gap-2 text-sm font-medium ${present ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600'}`}
        data-testid={`xero-bankfeed-trace-account-verdict-${mappingId}`}
      >
        {present ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
        <span>
          {present
            ? `Account id ${trace.xero_account_id} appears in the pull (${trace.candidates_on_mapped_account} candidate(s) on this account).`
            : `Account id ${trace.xero_account_id} never appears in the pull -- every transaction on this mapping is filtered out before amount/date matching ever runs.`}
        </span>
      </div>

      <div
        className="text-xs text-muted-foreground"
        data-testid={`xero-bankfeed-trace-mapped-account-count-${mappingId}`}
      >
        <span className="font-medium text-foreground">BankTransactions/Payments on the mapped account in this window: </span>
        {trace.candidates_on_mapped_account}
        {trace.candidates_on_mapped_account === 0 && ' (zero confirms the account is structurally unmappable in this pull, regardless of amount/date).'}
      </div>

      {trace.account_id_namespace_diagnosis && (
        <div
          className="text-xs border rounded-md p-2.5 space-y-1"
          data-testid={`xero-bankfeed-trace-namespace-diagnosis-${mappingId}`}
        >
          <div>
            <span className="font-medium text-foreground">Namespace: </span>
            {trace.account_id_namespace_diagnosis.namespace}
          </div>
          {trace.account_id_namespace_diagnosis.needs_migration && trace.account_id_namespace_diagnosis.current_account && (
            <div className="text-amber-600 dark:text-amber-400">
              Live account today: <code>{trace.account_id_namespace_diagnosis.current_account.name}</code>{' '}
              ({trace.account_id_namespace_diagnosis.current_account.account_id}) -- use the migration card
              below to fix this mapping.
            </div>
          )}
          {trace.account_id_namespace_diagnosis.needs_migration && !trace.account_id_namespace_diagnosis.current_account && (
            <div className="text-red-600">
              No live account resolves this id by AccountID, Code, or Name -- needs manual review.
            </div>
          )}
        </div>
      )}

      {trace.xero_candidates_by_account_resolved && trace.xero_candidates_by_account_resolved.length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid={`xero-bankfeed-trace-accounts-in-pull-${mappingId}`}>
          <span className="font-medium text-foreground">What IS posting in this pull: </span>
          {trace.xero_candidates_by_account_resolved.map((r) => (
            <span key={r.account_id} className="mr-2">
              {r.name || r.account_id} ({r.code || 'no code'}): {r.count}
            </span>
          ))}
          {typeof trace.xero_candidates_distinct_account_count === 'number'
            && trace.xero_candidates_distinct_account_count > trace.xero_candidates_by_account_resolved.length && (
            <span className="italic">
              (showing top {trace.xero_candidates_by_account_resolved.length} of {trace.xero_candidates_distinct_account_count} distinct accounts)
            </span>
          )}
        </div>
      )}

      {diagnosis && (
        <div
          className={`text-xs border rounded-md p-2.5 space-y-1 ${COVERAGE_DIAGNOSIS_STYLE[diagnosis.case] || ''}`}
          data-testid={`xero-bankfeed-trace-coverage-diagnosis-${mappingId}`}
        >
          <div className="font-medium text-foreground">Conclusion: {diagnosis.label}</div>
          <div>{diagnosis.detail}</div>
        </div>
      )}

      {diagnosis && diagnosis.case === 'valid_id_zero_coded' && trace.not_yet_coded_count !== null && (
        <div
          className="text-xs border rounded-md p-2.5"
          data-testid={`xero-bankfeed-trace-not-yet-coded-${mappingId}`}
        >
          <span className="font-medium text-foreground">Not yet coded in Xero: </span>
          {trace.not_yet_coded_count} FA transaction(s) in this window on this account with no Xero
          counterpart in the pull -- the honest accountant-workload number, read-only.
        </div>
      )}

      {trace.statement_lines && (
        <div
          className={`text-xs border rounded-md p-2.5 space-y-1 ${trace.statement_lines.available ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}
          data-testid={`xero-bankfeed-trace-statement-lines-${mappingId}`}
        >
          <div className="font-medium text-foreground">Bank statement lines (Finance API):</div>
          {trace.statement_lines.available ? (
            <div>
              {trace.statement_lines.count} line(s) synced -- {trace.statement_lines.reconciled_count} reconciled,{' '}
              {trace.statement_lines.unreconciled_count} unreconciled
              {trace.statement_lines.quarantined_count ? `, ${trace.statement_lines.quarantined_count} quarantined` : ''}.
              Last synced: {trace.statement_lines.last_synced_at || 'never'}. This account's Accounting
              column and "Not yet coded" filter are driven by these real statement lines, not the coverage
              inference above.
            </div>
          ) : (
            <div>
              Not available for this account
              {trace.statement_lines.last_error_reason === 'scope' && ' -- reconsent required (missing finance.bankstatementsplus.read scope).'}
              {trace.statement_lines.last_error_reason === 'account_not_found' && ' -- the Finance API rejected this account\'s BankAccountID.'}
              {!trace.statement_lines.last_error_reason && trace.statement_lines.last_error && ` -- ${trace.statement_lines.last_error}`}
              {!trace.statement_lines.last_error_reason && !trace.statement_lines.last_error && ' -- not yet synced.'}
              {' '}The coverage inference above is the fallback until this is available.
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Reason tally ({trace.sample_size_returned} sampled): </span>
        {reasonEntries.length === 0
          ? 'no transactions in the horizon window'
          : reasonEntries.map(([reason, count]) => `${reason}: ${count}`).join(', ')}
      </div>

      {trace.samples && trace.samples.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid={`xero-bankfeed-trace-table-${mappingId}`}>
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-1 pr-3">Transaction</th>
                <th className="py-1 pr-3">FA amount</th>
                <th className="py-1 pr-3">FA date</th>
                <th className="py-1 pr-3">Resolution</th>
                <th className="py-1 pr-3">Date delta</th>
                <th className="py-1 pr-3">Amount delta</th>
                <th className="py-1 pr-3">In date window</th>
                <th className="py-1 pr-3">In amount tolerance</th>
              </tr>
            </thead>
            <tbody>
              {trace.samples.map((s) => {
                const nearest = s.nearest_candidate_on_account;
                return (
                  <tr key={s.transaction_id} className="border-b last:border-0" data-testid={`xero-bankfeed-trace-row-${s.transaction_id}`}>
                    <td className="py-1 pr-3 font-mono">{s.transaction_id}</td>
                    <td className="py-1 pr-3">{s.fa_amount} {s.fa_currency}</td>
                    <td className="py-1 pr-3">{s.fa_date}</td>
                    <td className="py-1 pr-3">
                      <Badge variant={s.resolution === 'mapped' ? 'default' : 'outline'} className="text-[10px]">
                        {s.resolution === 'mapped' ? 'mapped' : (s.unmapped_reason || 'unmapped')}
                      </Badge>
                    </td>
                    <td className="py-1 pr-3">{nearest ? `${nearest.date_delta_days}d` : '-'}</td>
                    <td className="py-1 pr-3">{nearest ? nearest.amount_delta : '-'}</td>
                    <td className="py-1 pr-3">{nearest ? (nearest.within_date_window ? 'yes' : 'no') : '-'}</td>
                    <td className="py-1 pr-3">{nearest ? (nearest.within_amount_tolerance ? 'yes' : 'no') : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No sampled transactions in the horizon window.</p>
      )}
    </div>
  );
}

function MappingRow({ mapping, onRunNow, onDeactivate, onTraceStart, running }) {
  const lastRunStatus = mapping.last_run_status;
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceError, setTraceError] = useState(null);
  const [trace, setTrace] = useState(null);

  const tracePoll = useJobPolling({
    fetchStatus: (jobId) => xeroBankFeedAPI.matchTraceJobStatus(jobId),
    onCompleted: useCallback((result) => {
      if (result && result.ok) {
        setTrace(result);
        setTraceError(null);
      } else {
        setTrace(null);
        setTraceError((result && result.error) || 'Trace failed.');
      }
    }, []),
    onFailed: useCallback((error) => {
      // Job-level failure only (an unexpected exception, or the stale-
      // timeout auto-fail if the server restarted mid-poll) -- an
      // expected "Xero says no" outcome is a completed job with
      // result.ok=false, handled in onCompleted above, not here.
      setTrace(null);
      setTraceError(error);
    }, []),
  });

  // Resume a trace still in flight after a page refresh -- also opens the
  // panel so the running state is visible instead of polling silently
  // behind a collapsed row.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await xeroBankFeedAPI.matchTraceJobLatest(mapping.mapping_id);
        if (data.job && data.job.status === 'running') {
          setTraceOpen(true);
          tracePoll.start(data.job._id || data.job.id);
        }
      } catch {
        // Best-effort resume only -- a failed lookup here shouldn't block the row.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTraceClick = async () => {
    if (traceOpen) {
      setTraceOpen(false);
      return;
    }
    setTraceOpen(true);
    setTraceError(null);
    setTrace(null);
    try {
      const { data } = await onTraceStart(mapping.mapping_id);
      if (data.job_id) tracePoll.start(data.job_id);
    } catch (err) {
      setTraceError(getErrorMessage(err));
    }
  };

  const traceLoading = tracePoll.polling;

  return (
    <div className="border rounded-lg px-3 py-3 space-y-2" data-testid="xero-bankfeed-mapping-row">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span className="text-sm font-medium">{mapping.entity_name}</span>
          <span className="text-xs text-muted-foreground ml-2">
            Xero account <code>{mapping.xero_account_name || mapping.xero_account_id}</code>
            {' -> '}
            {mapping.internal_account_name || mapping.internal_bank_account_id}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastRunStatus && (
            <Badge variant={STATUS_VARIANT[lastRunStatus] || 'outline'} className="text-[10px]" data-testid="xero-bankfeed-status-badge">
              {STATUS_LABEL[lastRunStatus] || lastRunStatus}
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRunNow(mapping.mapping_id)}
            disabled={running}
            data-testid={`xero-bankfeed-run-now-${mapping.mapping_id}`}
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
            Run Now
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleTraceClick}
            data-testid={`xero-bankfeed-trace-${mapping.mapping_id}`}
          >
            {traceLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Search className="h-3.5 w-3.5 mr-1.5" />
            )}
            Trace
            {traceOpen ? <ChevronUp className="h-3.5 w-3.5 ml-1.5" /> : <ChevronDown className="h-3.5 w-3.5 ml-1.5" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDeactivate(mapping.mapping_id)}
            data-testid={`xero-bankfeed-deactivate-${mapping.mapping_id}`}
          >
            <PowerOff className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs text-muted-foreground">
        <div>
          <div className="font-medium text-foreground">Last run</div>
          <div>{mapping.last_run_at ? new Date(mapping.last_run_at).toLocaleString() : 'never'}</div>
        </div>
        <div>
          <div className="font-medium text-foreground">Mapped / unmapped</div>
          <div>{mapping.last_run_mapped ?? 0} / {mapping.last_run_unmapped ?? 0}</div>
        </div>
        <div>
          <div className="font-medium text-foreground">Reconciled in Xero</div>
          <div>{mapping.last_run_reconciled ?? 0}</div>
        </div>
        {/* CLAUDE.md Prompt V item 3: the accountant-workload number, one
            glance, on the row itself -- not only reachable via Trace. Only
            meaningful in coverage case (a) (services/xero_accounting_
            reconciliation.py::NOT_YET_CODED_COVERAGE_CASE); every other
            case renders "-" rather than a count that isn't backed by this
            mapping's own last-stamped diagnosis. */}
        <div>
          <div className="font-medium text-foreground">Not yet coded</div>
          <div data-testid={`xero-bankfeed-not-yet-coded-${mapping.mapping_id}`}>
            {mapping.coverage_diagnosis?.case === 'valid_id_zero_coded' && mapping.not_yet_coded_count !== null
              ? mapping.not_yet_coded_count
              : '-'}
          </div>
        </div>
        <div>
          <div className="font-medium text-foreground">Tenant</div>
          <div>{mapping.tenant_name || mapping.tenant_id}</div>
        </div>
      </div>

      {lastRunStatus === 'running' && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="xero-bankfeed-run-in-progress">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          Run in progress for this tenant...
        </div>
      )}

      {lastRunStatus === 'failed' && mapping.last_run_error && (
        <div className="flex items-center gap-2 text-xs text-red-600" data-testid="xero-bankfeed-run-error">
          <XCircle className="h-3.5 w-3.5 shrink-0" />
          {mapping.last_run_error}
        </div>
      )}

      {lastRunStatus === 'ok' && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          Feed is healthy. Display and ranking-boost only -- never auto-confirms a match.
        </div>
      )}

      {traceOpen && (
        <>
          {traceLoading && (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3 mt-2"
              data-testid={`xero-bankfeed-trace-loading-${mapping.mapping_id}`}
            >
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              Pulling live Xero candidates and tracing match predicates...
            </div>
          )}
          {!traceLoading && traceError && (
            <div
              className="flex items-center gap-2 text-xs text-red-600 border-t pt-3 mt-2"
              data-testid={`xero-bankfeed-trace-error-${mapping.mapping_id}`}
            >
              <XCircle className="h-3.5 w-3.5 shrink-0" />
              {traceError}
            </div>
          )}
          {!traceLoading && !traceError && trace && (
            <TraceResultPanel mappingId={mapping.mapping_id} trace={trace} />
          )}
        </>
      )}
    </div>
  );
}

const MIGRATION_OUTCOME_VARIANT = {
  would_migrate: 'default',
  migrated: 'default',
  no_change_needed: 'outline',
  conflict: 'destructive',
  unresolvable: 'destructive',
  error: 'destructive',
};

const MIGRATION_OUTCOME_LABEL = {
  would_migrate: 'would migrate',
  migrated: 'migrated',
  no_change_needed: 'no change needed',
  conflict: 'conflict',
  unresolvable: 'unresolvable',
  error: 'error',
};

/**
 * CLAUDE.md Prompt M item 2: Preview/Apply for migrating a mapping's stored
 * xero_account_id off a stale AccountID (an archived/reconnected bank feed
 * minted a new one) onto today's live account, resolved via services/
 * xero_bank_account_mapping.py::build_migration_plan. Preview never writes;
 * Apply re-derives the SAME plan and only applies would_migrate rows.
 */
function AccountIdMigrationCard({ onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const preview = async () => {
    setLoading(true);
    try {
      const { data } = await xeroBankFeedAPI.migrateAccountIdsPreview();
      setPlan(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const migratableCount = plan?.outcome_counts?.would_migrate || 0;
  // Nothing-to-migrate can mean two different things -- distinguish them
  // rather than asserting "ids are valid" when some rows are actually
  // blocked (conflict/unresolvable/error), which would be false.
  const needsAttentionCount = ['conflict', 'unresolvable', 'error']
    .reduce((sum, key) => sum + (plan?.outcome_counts?.[key] || 0), 0);

  const apply = async () => {
    if (!window.confirm(`Migrate ${migratableCount} mapping(s) to their current live Xero AccountID?`)) return;
    setApplying(true);
    try {
      const { data } = await xeroBankFeedAPI.migrateAccountIdsApply();
      setPlan(data);
      toast.success(`Migrated ${data.migrated} mapping(s).`);
      onApplied?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4" />
          Account-id migration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Checks every active mapping's stored Xero account id against the
          live chart of accounts (GET /Accounts) and names precisely which
          field it matches -- AccountID (healthy), Code, Name, or none (most
          likely a reconnected bank feed that minted a new AccountID).
          Read-only until you click Apply.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={preview} disabled={loading} data-testid="xero-bankfeed-migrate-preview">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Preview
          </Button>
          {migratableCount > 0 && (
            <Button size="sm" onClick={apply} disabled={applying} data-testid="xero-bankfeed-migrate-apply">
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Apply migration ({migratableCount})
            </Button>
          )}
          {plan && migratableCount === 0 && needsAttentionCount === 0 && (
            <span className="text-xs text-muted-foreground" data-testid="xero-bankfeed-migrate-nothing-to-migrate">
              Nothing to migrate: stored ids are valid AccountIDs.
            </span>
          )}
          {plan && migratableCount === 0 && needsAttentionCount > 0 && (
            <span className="text-xs text-red-600" data-testid="xero-bankfeed-migrate-nothing-to-migrate">
              Nothing auto-migratable: {needsAttentionCount} mapping(s) need manual review
              (conflict/unresolvable/error) -- see the table below.
            </span>
          )}
        </div>

        {plan && (
          <div className="overflow-x-auto" data-testid="xero-bankfeed-migrate-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-1 pr-3">Mapping</th>
                  <th className="py-1 pr-3">Stored id</th>
                  <th className="py-1 pr-3">Namespace</th>
                  <th className="py-1 pr-3">Outcome</th>
                  <th className="py-1 pr-3">Target account</th>
                  <th className="py-1 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {plan.mappings.map((row) => (
                  <tr key={row.mapping_id} className="border-b last:border-0" data-testid={`xero-bankfeed-migrate-row-${row.mapping_id}`}>
                    <td className="py-1 pr-3">{row.entity_id} / {row.tenant_name}</td>
                    <td className="py-1 pr-3 font-mono">{row.stored_xero_account_id}</td>
                    <td className="py-1 pr-3">{row.namespace}</td>
                    <td className="py-1 pr-3">
                      <Badge variant={MIGRATION_OUTCOME_VARIANT[row.outcome] || 'outline'} className="text-[10px]">
                        {MIGRATION_OUTCOME_LABEL[row.outcome] || row.outcome}
                      </Badge>
                    </td>
                    <td className="py-1 pr-3">
                      {row.target_account_id ? `${row.target_account_name || ''} (${row.target_account_id})` : '-'}
                    </td>
                    <td className="py-1 pr-3">{row.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function XeroBankFeedStatusPanel() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await xeroBankFeedAPI.listMappings();
      const loaded = data.mappings || [];
      setMappings(loaded);
      return loaded;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const poll = useJobPolling({
    fetchStatus: (jobId) => xeroBankFeedAPI.runNowJobStatus(jobId),
    onCompleted: (result) => {
      if (result?.status === 'ok') {
        toast.success(`Run complete: ${result.mapped ?? 0} mapped, ${result.unmapped ?? 0} unmapped.`);
      } else {
        toast.error(`Run failed: ${result?.error || 'unknown error'}`);
      }
      load();
    },
    onFailed: (error) => {
      toast.error(error);
      load();
    },
  });

  useEffect(() => {
    (async () => {
      const loaded = await load();
      // Resume polling a tenant run still in flight after a page refresh --
      // every mapping under a running tenant carries the same job id
      // (Run Now is stamped last_run_status="running" on every mapping
      // under the tenant before the route returns).
      const inFlight = loaded.find((m) => m.last_run_status === 'running' && m.last_run_job_id);
      if (inFlight) poll.start(inFlight.last_run_job_id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runNow = async (mappingId) => {
    try {
      const { data } = await xeroBankFeedAPI.runNow(mappingId);
      if (data.status === 'already_running') {
        toast.message('A run is already in progress for this tenant.');
      }
      if (data.job_id) poll.start(data.job_id);
      // Reload immediately -- the route stamps every mapping under the
      // tenant last_run_status="running" before returning, so this
      // reflects the in-flight attempt on every row right away instead of
      // waiting for the poll's first tick.
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const deactivate = async (mappingId) => {
    try {
      await xeroBankFeedAPI.deactivateMapping(mappingId);
      toast.success('Mapping deactivated.');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Starting the trace job can itself reject (network/auth) -- left to
  // reject here so MappingRow's own try/catch renders it inline via
  // getErrorMessage, never a toast, so a failure shows up right under the
  // row that triggered it. Once started, MappingRow polls job-status
  // itself (matchTraceJobStatus/matchTraceJobLatest) rather than awaiting
  // the trace result here -- same out-of-band shape as Run Now above.
  const traceStart = (mappingId) => xeroBankFeedAPI.matchTraceStart(mappingId);

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          Xero Accounting Reconciliation (Phase 2B)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Connection mappings from a Xero bank account to an internal BR OS
          bank account, with daily read-only reconciliation-state pull
          status. Display and ranking-boost only -- structurally never
          auto-confirms a match. One active mapping per internal account is
          enforced at mapping time.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading mappings...
          </div>
        ) : mappings.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="xero-bankfeed-no-mappings">
            No Xero bank-account mappings configured yet.
          </p>
        ) : (
          <div className="space-y-2" data-testid="xero-bankfeed-mapping-list">
            {mappings.map((m) => (
              <MappingRow
                key={m.mapping_id}
                mapping={m}
                onRunNow={runNow}
                onDeactivate={deactivate}
                onTraceStart={traceStart}
                running={m.last_run_status === 'running'}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setConfigureOpen(true)} data-testid="xero-bankfeed-configure-open">
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Configure
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </CardContent>

      <XeroBankFeedConfigureModal
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        onSaved={load}
      />
    </Card>
    <AccountIdMigrationCard onApplied={load} />
    </div>
  );
}
